import useTranslation from '../useTranslation';
import { BoundaryPartRequestBoundaryType } from 'generatedTypes/flight/models';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { hectaresToAcres, squareMetersToAcres } from 'utils/areaUtil';
import { litersToGallons } from 'utils/volumeUtil';

import { DEFAULT_CENTER, DEFAULT_ZOOM, MAP_STYLE } from 'components/Map/constants';

import useApplication from 'hooks/useApplication';
import useBoundary from 'hooks/useBoundary';
import useFlights from 'hooks/useFlights';
import { useFloatingResourceAddContext } from 'hooks/useFloatingResourceAddContext';
import { useNavigationCollapse } from 'hooks/useNavigationCollapse';
import usePageRoutes from 'hooks/usePageRoutes';

import {
    DETAIL_TYPE_BOUNDARY,
    DETAIL_TYPE_CLIENT,
    DETAIL_TYPE_ESTIMATED_ACRES,
    DETAIL_TYPE_FARM,
    DETAIL_TYPE_FIELD,
    DETAIL_TYPE_TOTAL_AREA_APPLIED,
    DETAIL_TYPE_TOTAL_VOLUME_APPLIED,
    TIMELINE_PAGE_SIZE
} from 'pages/ApplicationDetailsPage/constants';

import type {
    ApplicationResponse,
    BoundaryResponse,
    CreateBoundaryRequest,
    DetailsViewEntityLinkProps,
    DetailsViewEntityMenuItem,
    FlightResponse,
    Geometry,
    MapInteractionOptions,
    MapOptions
} from './types';

const useData = () => {
    const { applicationId } = useParams();
    const { state } = useLocation();
    const { ACRES, EDIT_APPLICATION, GALLONS } = useTranslation();
    const {
        assignBoundaryToApplication,
        fetchApplication,
        isLoading: isApplicationLoading
    } = useApplication();
    const { createBoundary, fetchBoundary, isLoading: isBoundaryLoading } = useBoundary();
    const { fetch: fetchFlights, flights = [], isLoading: isFlightLoading } = useFlights();

    const {
        applicationsPage,
        batteryDetailsPage,
        boundaryDetailsPage,
        clientDetailsPage,
        droneDetailsPage,
        farmDetailsPage,
        fieldDetailsPage,
        userDetailsPage
    } = usePageRoutes();
    const navigate = useNavigate();
    const [currentApplication, setCurrentApplication] = useState<ApplicationResponse | undefined>(
        undefined
    );

    const [isEditingMap, setIsEditingMap] = useState(state?.isEditingMap === true);
    const [droneMenuItems, setDroneMenuItems] = useState<DetailsViewEntityMenuItem[]>([]);
    const [batteryMenuItems, setBatteryMenuItems] = useState<DetailsViewEntityMenuItem[]>([]);
    const [scheduledPilotMenuItems, setScheduledPilotMenuItems] = useState<
        DetailsViewEntityMenuItem[]
    >([]);
    const [pilotMenuItems, setPilotMenuItems] = useState<DetailsViewEntityMenuItem[]>([]);
    const [boundary, setBoundary] = useState<BoundaryResponse | undefined>(undefined);
    const [hasMoreFlights, setHasMoreFlights] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState<FlightResponse | undefined>(undefined);

    useFloatingResourceAddContext(false);
    useNavigationCollapse(isEditingMap);

    const handleEditMapClick = () => {
        setIsEditingMap(true);
    };

    const handleEditMapClose = () => {
        setIsEditingMap(false);
    };
    const [isActivityOpen, setIsActivityOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditingForm, setIsEditingForm] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const estimatedAcresEntityName = !boundary?.areaSqM
        ? ''
        : `${squareMetersToAcres(boundary.areaSqM)} ${ACRES}`;

    const totalVolumeAppliedEntityName = !currentApplication?.totalVolumeAppliedLiters
        ? ''
        : `${litersToGallons(currentApplication.totalVolumeAppliedLiters)} ${GALLONS}`;

    const totalAreaAppliedEntityName = !currentApplication?.totalAreaAppliedHectares
        ? ''
        : `${hectaresToAcres(currentApplication.totalAreaAppliedHectares)} ${ACRES}`;

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentApplication?.client?.name ?? '',
            entityRoute: clientDetailsPage(`${currentApplication?.client?.id}`),
            entityType: DETAIL_TYPE_CLIENT
        },

        {
            entityName: currentApplication?.parcel?.name ?? '',
            entityRoute: farmDetailsPage(`${currentApplication?.parcel?.id}`),
            entityType: DETAIL_TYPE_FARM
        },
        {
            entityName: currentApplication?.site?.name ?? '',
            entityRoute: fieldDetailsPage(`${currentApplication?.site?.id}`),
            entityType: DETAIL_TYPE_FIELD
        },
        {
            entityName: boundary?.name ?? '',
            entityRoute: boundary?.id ? boundaryDetailsPage(boundary.id) : undefined,
            entityType: DETAIL_TYPE_BOUNDARY
        },
        {
            entityName: estimatedAcresEntityName,
            entityRoute: undefined,
            entityType: DETAIL_TYPE_ESTIMATED_ACRES
        },
        {
            entityName: totalVolumeAppliedEntityName,
            entityRoute: undefined,
            entityType: DETAIL_TYPE_TOTAL_VOLUME_APPLIED
        },
        {
            entityName: totalAreaAppliedEntityName,
            entityRoute: undefined,
            entityType: DETAIL_TYPE_TOTAL_AREA_APPLIED
        }
    ];

    useEffect(() => {
        if (!currentApplication) {
            return;
        }

        const getDetailsMenuItems = async () => {
            if (currentApplication.drones) {
                setDroneMenuItems(
                    currentApplication.drones.map(drone => ({
                        entityId: drone?.id ?? '',
                        entityName: drone?.name ?? '',
                        entityRoute: droneDetailsPage(`${drone?.id}`)
                    }))
                );
            } else {
                setDroneMenuItems([]);
            }

            if (currentApplication.batteries) {
                setBatteryMenuItems(
                    currentApplication.batteries.map(battery => ({
                        entityId: battery?.id ?? '',
                        entityName: battery?.name ?? '',
                        entityRoute: batteryDetailsPage(`${battery?.id}`)
                    }))
                );
            } else {
                setBatteryMenuItems([]);
            }

            if (currentApplication.scheduledPilots) {
                setScheduledPilotMenuItems(
                    currentApplication.scheduledPilots.map(pilot => ({
                        entityId: pilot?.id ?? '',
                        entityName: `${pilot?.firstName ?? ''} ${pilot?.lastName ?? ''}`.trim(),
                        entityRoute: userDetailsPage(`${pilot?.id}`)
                    }))
                );
            } else {
                setScheduledPilotMenuItems([]);
            }

            if (currentApplication.pilots) {
                setPilotMenuItems(
                    currentApplication.pilots.map(pilot => ({
                        entityId: pilot?.id ?? '',
                        entityName: `${pilot?.firstName ?? ''} ${pilot?.lastName ?? ''}`.trim(),
                        entityRoute: userDetailsPage(`${pilot?.id}`)
                    }))
                );
            } else {
                setPilotMenuItems([]);
            }
        };

        getDetailsMenuItems();

        const getBoundary = async () => {
            const boundary = await fetchBoundary({ id: currentApplication.boundaryId ?? '' });

            setBoundary(boundary ?? undefined);
        };

        if (currentApplication.boundaryId) {
            getBoundary();
        }

        const getFlights = async () => {
            const flightsGenerator = fetchFlights({
                applicationId,
                pageSize: TIMELINE_PAGE_SIZE,
                sort: [{ direction: 'ASC', field: 'startTime' }]
            });

            const { done } = await flightsGenerator.next();

            setHasMoreFlights(!done);
        };

        if (currentApplication.id) {
            getFlights();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentApplication]);

    const geometryParts = useMemo(
        () =>
            boundary?.boundaryParts?.map(part => ({
                ...part,
                applicationName:
                    part.boundaryType === BoundaryPartRequestBoundaryType.APPLY
                        ? currentApplication?.name
                        : undefined
            })) ?? [],
        [boundary, currentApplication?.name]
    );

    const geometry: Geometry = useMemo(
        () => ({
            isEditable: isEditingMap,
            parts: geometryParts
        }),
        [geometryParts, isEditingMap]
    );

    const mapOptions: MapOptions = {
        accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
        center: DEFAULT_CENTER,
        mapStyle: MAP_STYLE,
        zoom: DEFAULT_ZOOM
    };

    const mapInteractionOptions: MapInteractionOptions = {
        hasAddressSearch: true,
        hasDrawingControls: isEditingMap,
        hasLayerToggles: true,
        hasMapControls: true
    };

    const handleMapComplete = async (result: CreateBoundaryRequest) => {
        if (!applicationId || !currentApplication?.name || !result) {
            setIsEditingMap(false);

            return;
        }

        const boundaryName = `${currentApplication.name} - ${result.name}`;
        const createdBoundary = await createBoundary({
            boundary: { boundaryParts: result.boundaryParts, name: boundaryName }
        });

        if (!createdBoundary) {
            setIsEditingMap(false);

            return;
        }

        await assignBoundaryToApplication({
            applicationId,
            boundaryId: createdBoundary.id
        });

        setBoundary(createdBoundary);
        setIsEditingMap(false);
    };

    const getCurrentApplication = async () => {
        if (applicationId) {
            const application = await fetchApplication({ id: applicationId });

            setCurrentApplication(application ?? undefined);
        }
    };

    useEffect(() => {
        if (applicationId) {
            getCurrentApplication();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    const handleNextFlightsPage = async () => {
        const { done } = await fetchFlights({
            applicationId,
            pageSize: TIMELINE_PAGE_SIZE,
            sort: [{ direction: 'ASC', field: 'startTime' }]
        }).next();

        setHasMoreFlights(!done);
    };

    const handleEditClick = () => {
        setIsEditingForm(true);
    };

    const handleToggleActivity = () => {
        setIsActivityOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditingForm) {
            setIsEditingForm(false);
            setIsClosing(false);
            getCurrentApplication();
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_APPLICATION
        }
    ];

    const handleNavigateBack = () => {
        navigate(applicationsPage);
    };

    const handleClose = () => {
        if (isEditingForm) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    const handleSelectFlight = (flight: FlightResponse) => {
        setSelectedFlight(flight);
    };

    return {
        batteryMenuItems,
        boundary,
        currentApplication,
        droneMenuItems,
        dropdownMenuItems,
        entitiesLink,
        flights,
        geometry,
        handleClose,
        handleEditClose,
        handleEditMapClick,
        handleEditMapClose,
        handleMapComplete,
        handleNextFlightsPage,
        handleSelectFlight,
        handleToggleActivity,
        handleToggleLinkedEntities,
        hasMoreFlights,
        isActivityOpen,
        isApplicationLoading,
        isBoundaryLoading,
        isClosing,
        isEditingForm,
        isEditingMap,
        isFlightLoading,
        isLinkedEntitiesOpen,
        mapInteractionOptions,
        mapOptions,
        pilotMenuItems,
        scheduledPilotMenuItems,
        selectedFlight,
        setIsClosing
    };
};

export default useData;
